// BUILD-046 section C (Owner direction of 2026-10-21): the "Needs attention" order and the attention condition each task shows.
//
// Five groups, in this order, and each task appears ONCE, in the highest group it belongs to:
//   1  a MISSED follow-up      the next follow-up date is before today (Phoenix)
//   2  a follow-up TODAY       the next follow-up date is today
//   3  OVERDUE                  the one overdue rule (overdueRule.ts)
//   4  DUE TODAY               the task due date is today
//   5  everything else
// CHANGED 2026-10-23 (BUILD-046 G4, Builder decision requester-edit-authority-final-local-check, October 23 addendum, item 2): a follow-up that is due now (missed, then today) comes BEFORE ordinary overdue; it used to come after it, so a
// task whose deadline had passed hid the follow-up that had just come due. The follow-up never changes the deadline or clears overdue: a task in group 1 or 2 that is also overdue is STILL overdue and its row says both (`attentionText`).
// Only an OPEN task whose assignment is live and that is shown in full has a condition; a completed, ended or restricted task is in group 5 and shows none. Inside a group the order is
// fixed and total, so the same tasks always come out in the same order: the date that put the task in the group (earliest first; none last), the priority (Urgent first), the task due
// date (earliest first; none last), the title A to Z, then the task id. "Recommended" is a different, separate choice and is not changed by this.
//
// Pure module. Runtime imports: the overdue rule, the date check and the priority ranks. Erasable TypeScript only.
import { isIsoDate } from "./dateLabels";
import { assignmentEnded, isOpenStatus, isOverdueTask } from "./overdueRule";
import type { OverdueTask } from "./overdueRule";
import { PRIORITY_RANK } from "./taskOrdering";

export type AttentionLevel = "missed-follow-up" | "overdue" | "follow-up-today" | "due-today" | "none";

export const ATTENTION_RANK: Readonly<Record<AttentionLevel, number>> = Object.freeze({ "missed-follow-up": 1, "follow-up-today": 2, overdue: 3, "due-today": 4, none: 5 });

/** The words of each condition, short enough for a row. */
export const ATTENTION_WORDS: Readonly<Record<Exclude<AttentionLevel, "none">, string>> = Object.freeze({
  "missed-follow-up": "Follow-up missed",
  overdue: "Overdue",
  "follow-up-today": "Follow-up today",
  "due-today": "Due today",
});

export const NEEDS_ATTENTION_ORDER_TEXT = "Missed follow-ups, follow-ups today, overdue, due today, then everything else";

export interface AttentionTask extends OverdueTask {
  id?: unknown;
  title?: unknown;
  priority?: unknown;
  followUpDate?: unknown;
}

const dateOf = (value: unknown): string => (typeof value === "string" && isIsoDate(value) ? value : "");

function eligible(task: AttentionTask): boolean {
  return task.redacted !== true && isOpenStatus(task.status) && !assignmentEnded(task);
}

/** The highest group this task belongs to on the Phoenix day `today` (YYYY-MM-DD). */
export function attentionLevelOf(task: AttentionTask, today: string): AttentionLevel {
  if (!eligible(task)) return "none";
  const follow = dateOf(task.followUpDate);
  const due = dateOf(task.dueDate);
  if (follow !== "" && follow < today) return "missed-follow-up";
  if (follow !== "" && follow === today) return "follow-up-today";
  if (isOverdueTask(task, today)) return "overdue";
  if (due !== "" && due === today) return "due-today";
  return "none";
}

/** Where the follow-up stands for an eligible task on the Phoenix day `today`: missed (before today), today, or none (no date, a later date, or a task that is not eligible). */
export function followUpConditionOf(task: AttentionTask, today: string): "missed" | "today" | null {
  if (!eligible(task)) return null;
  const follow = dateOf(task.followUpDate);
  if (follow === "") return null;
  return follow < today ? "missed" : follow === today ? "today" : null;
}

/** Where the deadline stands for an eligible task: overdue (THE one rule), due today, or none. Independent of the follow-up date. */
export function deadlineConditionOf(task: AttentionTask, today: string): "overdue" | "due-today" | null {
  if (!eligible(task)) return null;
  if (isOverdueTask(task, today)) return "overdue";
  const due = dateOf(task.dueDate);
  return due !== "" && due === today ? "due-today" : null;
}

/**
 * The "Follow-up due" filter (BUILD-046 G4): an UNFINISHED task whose follow-up date is today or has passed. A completed, ended or restricted task is never kept, and a task with no follow-up date, or a later one, is not. It reads
 * the follow-up date only: the deadline decides nothing here, and the follow-up date decides nothing about the deadline.
 */
export function followUpDueOn(task: AttentionTask, today: string): boolean {
  return followUpConditionOf(task, today) !== null;
}

/** The short next-action text a row shows beside a due follow-up (the task's own words, trimmed), or null when it has none or is shown in its restricted form. */
export function nextActionOf(task: { nextAction?: unknown; redacted?: unknown }): string | null {
  if (task.redacted === true) return null;
  return typeof task.nextAction === "string" && task.nextAction.trim() !== "" ? task.nextAction.trim() : null;
}

/** The group number, 1 to 5. */
export function attentionRankOf(task: AttentionTask, today: string): number {
  return ATTENTION_RANK[attentionLevelOf(task, today)];
}

/**
 * The short words a row shows for its conditions, or null when it has none. CHANGED 2026-10-23 (BUILD-046 G4, October 23 addendum, item 2): BOTH conditions are shown, the follow-up first, then the deadline, joined by a middle dot
 * ("Follow-up today · Overdue"), so a follow-up that has come due never hides an overdue deadline and an overdue deadline never hides the follow-up. A missed follow-up carries its date.
 */
export function attentionText(task: AttentionTask, today: string, formatDate: (date: string) => string = (date) => date, options: { omitOverdue?: boolean } = {}): string | null {
  const parts: string[] = [];
  const follow = followUpConditionOf(task, today);
  if (follow === "missed") parts.push(ATTENTION_WORDS["missed-follow-up"] + " " + formatDate(dateOf(task.followUpDate)));
  else if (follow === "today") parts.push(ATTENTION_WORDS["follow-up-today"]);
  const deadline = deadlineConditionOf(task, today);
  // BUILD-046 Team table cleanup: a row whose Due column already WRITES "Overdue" (dueLabel.dueCellStatesOverdue) leaves the word out here, so it is not said twice. ONLY the literal word is
  // left out: a follow-up warning, "Due today" and every other condition are kept, and a caller that does not ask (every other table) gets exactly what it always got.
  if (deadline !== null && !(options.omitOverdue === true && deadline === "overdue")) parts.push(ATTENTION_WORDS[deadline]);
  return parts.length === 0 ? null : parts.join(" \u00b7 ");
}

const COLLATOR = new Intl.Collator("en", { sensitivity: "base", numeric: true });
const LAST = "9999-12-31";

function priorityOf(task: AttentionTask): number {
  return typeof task.priority === "string" && Object.prototype.hasOwnProperty.call(PRIORITY_RANK, task.priority) ? PRIORITY_RANK[task.priority] : 0;
}

function groupDate(task: AttentionTask, level: AttentionLevel): string {
  const date = level === "missed-follow-up" ? dateOf(task.followUpDate) : level === "overdue" || level === "none" ? dateOf(task.dueDate) : "";
  return date === "" ? LAST : date;
}

/** The tasks in the "Needs attention" order. A new array; the input is not changed. The order is total (the id is the last tie-break), so it never depends on the incoming order. */
export function attentionOrder<T extends AttentionTask>(tasks: readonly T[], today: string): T[] {
  const keyed = tasks.map((task) => ({ task, level: attentionLevelOf(task, today) }));
  keyed.sort((a, b) => {
    const byGroup = ATTENTION_RANK[a.level] - ATTENTION_RANK[b.level];
    if (byGroup !== 0) return byGroup;
    const byDate = groupDate(a.task, a.level).localeCompare(groupDate(b.task, b.level));
    if (byDate !== 0) return byDate;
    const byPriority = priorityOf(b.task) - priorityOf(a.task);
    if (byPriority !== 0) return byPriority;
    const dueA = dateOf(a.task.dueDate) || LAST;
    const dueB = dateOf(b.task.dueDate) || LAST;
    const byDue = dueA.localeCompare(dueB);
    if (byDue !== 0) return byDue;
    const byTitle = COLLATOR.compare(typeof a.task.title === "string" ? a.task.title : "", typeof b.task.title === "string" ? b.task.title : "");
    if (byTitle !== 0) return byTitle;
    const idA = typeof a.task.id === "string" ? a.task.id : "";
    const idB = typeof b.task.id === "string" ? b.task.id : "";
    return idA < idB ? -1 : idA > idB ? 1 : 0;
  });
  return keyed.map((entry) => entry.task);
}
